import React, { useEffect, useMemo, useState } from 'react';
import { Card, Title, Text, Stack, Group, Table, Loader, Pagination, rem, Badge, MultiSelect, ActionIcon, Tooltip, Select } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { DateTimePicker } from '@mantine/dates';
import { useMediaQuery } from '@mantine/hooks';
import { IconDownload } from '@tabler/icons-react';
import { fetchMeasurements, searchGateways, searchSensors, listMetricDefs } from '../services/dataService';
import { useDeviceFilter } from '../hooks/useDeviceFilter';
import type { DataQuery, MetricDef } from '../types';
import type { PivotedMeasurementRecord } from '../types/pivoted';
import { useSearchParams } from 'react-router-dom';

const MeasurementsPage: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 48em)');
  const [searchParams] = useSearchParams();

  // Device filter hook
  const deviceFilter = useDeviceFilter({
    initialGateways: searchParams.getAll('gatewayId'),
    initialSensors: searchParams.getAll('sensorId'),
  });

  // Defaults: last 1 month
  const now = useMemo(() => new Date(), []);
  const oneMonthAgo = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d;
  }, []);

  const [query, setQuery] = useState<DataQuery>({
    page: 1,
    pageSize: 10,
    from: oneMonthAgo.toISOString(),
    to: now.toISOString(),
  });

  const [data, setData] = useState<PivotedMeasurementRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [gatewaySearch, setGatewaySearch] = useState('');
  const [sensorSearch, setSensorSearch] = useState('');
  const [gatewayLoading, setGatewayLoading] = useState(false);
  const [sensorLoading, setSensorLoading] = useState(false);
  const [metricDefs, setMetricDefs] = useState<MetricDef[]>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);

  // Map sensor id -> valueTypeName and metricId from device directory
  const [sensorValueTypeById, setSensorValueTypeById] = useState<Map<string, string>>(new Map());
  const [sensorMetricIdById, setSensorMetricIdById] = useState<Map<string, string>>(new Map());

  // metricId -> AssetValueType.name derived from sensor assets
  const metricIdToValueTypeName = useMemo(() => {
    const map = new Map<string, string>();
    sensorMetricIdById.forEach((metricId, sensorId) => {
      const vt = sensorValueTypeById.get(sensorId);
      if (metricId && vt) map.set(metricId, vt);
    });
    return map;
  }, [sensorValueTypeById, sensorMetricIdById]);

  // Translate metric id: prefer AssetValueType.name, fallback to i18n
  const metricDefById = useMemo(() => Object.fromEntries(metricDefs.map((d) => [d.id, d])), [metricDefs]);
  const translateMetricId = React.useCallback((id?: string): string => {
    if (!id) return '-';
    const vt = metricIdToValueTypeName.get(id);
    if (vt) return vt;
    const def = metricDefById[id];
    if (!def) return id;
    const label = t(def.tKey);
    return (label && label !== def.tKey ? (label as string) : id);
  }, [metricDefById, metricIdToValueTypeName, t]);

  const metricOptions = useMemo(
    () => deviceFilter.valueTypeOptions,
    [deviceFilter.valueTypeOptions]
  );

  // preload metric defs and read initial URL params
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setGatewayLoading(true);
        setSensorLoading(true);
        const defsRes = await listMetricDefs();
        if (!cancelled) {
          setMetricDefs(defsRes);
          
          // Build sensor maps from device filter
          if (deviceFilter.devicesDir) {
            setSensorValueTypeById(() => {
              const next = new Map<string, string>();
              (deviceFilter.devicesDir!.sensors || []).forEach((d) => { if (d.valueTypeName) next.set(d.id, d.valueTypeName); });
              return next;
            });
            setSensorMetricIdById(() => {
              const next = new Map<string, string>();
              (deviceFilter.devicesDir!.sensors || []).forEach((d) => { if (d.metricId) next.set(d.id, d.metricId); });
              return next;
            });
          }
          
          // parse initial URL params
          const gw = searchParams.getAll('gatewayId');
          const sn = searchParams.getAll('sensorId');
          const met = searchParams.getAll('metric');
          const from = searchParams.get('from');
          const to = searchParams.get('to');
          setSelectedMetrics(met);
          deviceFilter.setSelectedValueTypes(met);
          setQuery((q) => ({
            ...q,
            gatewayId: gw.length ? gw : undefined,
            sensorId: sn.length ? sn : undefined,
            metric: met.length ? met : undefined,
            from: from || q.from,
            to: to || q.to,
            page: 1,
          }));
        }
      } finally {
        if (!cancelled) {
          setGatewayLoading(false);
          setSensorLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, t, deviceFilter.devicesDir]);

  // Warn if user tries to leave while export is in progress
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (exportLoading) {
        e.preventDefault();
        e.returnValue = 'Der CSV-Export läuft noch. Möchtest du die Seite wirklich verlassen?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [exportLoading]);

  // Update sensor maps when device directory changes
  useEffect(() => {
    if (deviceFilter.devicesDir) {
      setSensorValueTypeById(() => {
        const next = new Map<string, string>();
        (deviceFilter.devicesDir!.sensors || []).forEach((d) => { if (d.valueTypeName) next.set(d.id, d.valueTypeName); });
        return next;
      });
      setSensorMetricIdById(() => {
        const next = new Map<string, string>();
        (deviceFilter.devicesDir!.sensors || []).forEach((d) => { if (d.metricId) next.set(d.id, d.metricId); });
        return next;
      });
    }
  }, [deviceFilter.devicesDir]);

  // Get filtered sensor IDs from the device filter hook
  const filteredSensorIds = useMemo(() => 
    deviceFilter.filteredSensors.map(s => s.id),
    [deviceFilter.filteredSensors]
  );

  // Fetch measurements (always in pivoted format) - filter by sensor IDs from hook
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    
    // Use filtered sensor IDs from hook if any filters are active, otherwise use query.sensorId
    const effectiveSensorIds = filteredSensorIds.length > 0 ? filteredSensorIds : 
      (Array.isArray(query.sensorId) ? query.sensorId : (query.sensorId ? [query.sensorId] : undefined));
    
    fetchMeasurements({ 
      ...query, 
      sensorId: effectiveSensorIds,
      gatewayId: undefined,
      metric: undefined
    })
      .then((res) => {
        if (cancelled) return;
        setData(res.items);
        setTotal(res.total);
      })
      .catch((e) => !cancelled && setError(String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [query, selectedMetrics, filteredSensorIds]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / (query.pageSize ?? 10))), [total, query.pageSize]);

  return (
    <Stack gap="lg">
      <Title order={2}>{t('navigation.measurements') || 'Messwerte'}</Title>

      {/* Filter */}
      <Card withBorder p="md">
        <Stack gap="sm">
          <Group gap="sm" grow wrap="wrap">
            <DateTimePicker
              label={t('data.filters.from') || 'Von'}
              placeholder="Datum & Zeit wählen"
              value={query.from ?? null}
              onChange={(value: string | null) => setQuery((q) => ({ ...q, from: value || undefined, page: 1 }))}
              firstDayOfWeek={1}
              withSeconds={false}
              valueFormat="DD.MM.YYYY HH:mm"
              dropdownType="popover"
              clearable
            />
            <DateTimePicker
              label={t('data.filters.to') || 'Bis'}
              placeholder="Datum & Zeit wählen"
              value={query.to ?? null}
              onChange={(value: string | null) => setQuery((q) => ({ ...q, to: value || undefined, page: 1 }))}
              firstDayOfWeek={1}
              withSeconds={false}
              valueFormat="DD.MM.YYYY HH:mm"
              dropdownType="popover"
              clearable
            />
          </Group>
          <Group gap="sm" grow wrap="wrap">
            <MultiSelect
              label={t('data.filters.metrics') || 'Metriken'}
              placeholder={t('common.select') || 'Auswählen'}
              data={metricOptions}
              value={selectedMetrics}
              onChange={(vals) => { 
                setSelectedMetrics(vals); 
                deviceFilter.setSelectedValueTypes(vals);
                setQuery((q) => ({ ...q, page: 1 })); 
              }}
              searchable
              nothingFoundMessage={t('common.none') || 'Nichts gefunden'}
              clearable
            />
          </Group>
          <Group gap="sm" grow wrap="wrap">
            <MultiSelect
              label={t('data.filters.gateway') || 'Gateway-ID'}
              placeholder="gw-1"
              data={deviceFilter.gatewayOptions}
              value={Array.isArray(query.gatewayId) ? query.gatewayId : (query.gatewayId ? [query.gatewayId] : [])}
              onChange={(vals) => {
                deviceFilter.setSelectedGateways(vals);
                setQuery((q) => ({ ...q, gatewayId: vals.length ? vals : undefined, page: 1 }));
              }}
              searchable
              searchValue={gatewaySearch}
              onSearchChange={async (s) => {
                setGatewaySearch(s);
                setGatewayLoading(true);
                try {
                  const items = await searchGateways(s);
                  // Gateway options now managed by hook
                } finally {
                  setGatewayLoading(false);
                }
              }}
              nothingFoundMessage={t('common.none') || 'Nichts gefunden'}
              rightSection={gatewayLoading || deviceFilter.loading ? <Loader size="xs" /> : null}
              clearable
            />
            <MultiSelect
              label={t('data.filters.sensor') || 'Sensor-ID'}
              placeholder="sn-1"
              data={deviceFilter.sensorOptions}
              value={Array.isArray(query.sensorId) ? query.sensorId : (query.sensorId ? [query.sensorId] : [])}
              onChange={(vals) => {
                deviceFilter.setSelectedSensors(vals);
                setQuery((q) => ({ ...q, sensorId: vals.length ? vals : undefined, page: 1 }));
              }}
              searchable
              searchValue={sensorSearch}
              onSearchChange={async (s) => {
                setSensorSearch(s);
                setSensorLoading(true);
                try {
                  // Pass gatewayId to filter sensors by gateway
                  const selectedGateway = Array.isArray(query.gatewayId) ? query.gatewayId[0] : query.gatewayId;
                  const items = await searchSensors(s, 20, selectedGateway);
                  // Sensor options now managed by hook
                } finally {
                  setSensorLoading(false);
                }
              }}
              nothingFoundMessage={t('common.none') || 'Nichts gefunden'}
              rightSection={sensorLoading || deviceFilter.loading ? <Loader size="xs" /> : null}
              clearable
            />
          </Group>
        </Stack>
      </Card>

      {/* Data table */}
      <Card withBorder p="0">
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: rem(32) }}>
            <Loader />
          </div>
        ) : error ? (
          <div style={{ padding: rem(16) }}>
            <Text c="red">{error}</Text>
          </div>
        ) : (
          isMobile ? (
            <Stack p="sm" gap="sm">
              {data.length === 0 ? (
                <Text c="dimmed">{t('data.table.noData') || 'Keine Daten gefunden'}</Text>
              ) : (
                data.map((r) => (
                  <Card key={r.id} withBorder p="sm">
                    <Stack gap={4}>
                      <Group justify="space-between">
                        <Text fw={600}>{new Date(r.timestamp).toLocaleString()}</Text>
                      </Group>
                      <Text size="sm">{r.gateway ? r.gateway.name : '-'}</Text>
                      <Text size="sm">{r.sensor ? r.sensor.name : '-'}</Text>
                      {r.temperature !== null && r.temperature !== undefined && (
                        <Group justify="space-between" mt={4}>
                          <Text c="dimmed" size="sm">{translateMetricId('temperature')}</Text>
                          <Text fw={600}>{r.temperature} °C</Text>
                        </Group>
                      )}
                      {r.humidity_general !== null && r.humidity_general !== undefined && (
                        <Group justify="space-between">
                          <Text c="dimmed" size="sm">{translateMetricId('humidity.general')}</Text>
                          <Text fw={600}>{r.humidity_general} %</Text>
                        </Group>
                      )}
                      {r.battery !== null && r.battery !== undefined && (
                        <Group justify="space-between">
                          <Text c="dimmed" size="sm">{translateMetricId('battery')}</Text>
                          <Text fw={600}>{r.battery} %</Text>
                        </Group>
                      )}
                      {r.spannung !== null && r.spannung !== undefined && (
                        <Group justify="space-between">
                          <Text c="dimmed" size="sm">{translateMetricId('spannung')}</Text>
                          <Text fw={600}>{r.spannung} V</Text>
                        </Group>
                      )}
                    </Stack>
                  </Card>
                ))
              )}
            </Stack>
          ) : (
            <Table verticalSpacing="sm" striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('data.table.time') || 'Zeit'}</Table.Th>
                  <Table.Th>{t('data.table.gateway') || 'Gateway'}</Table.Th>
                  <Table.Th>{t('data.table.sensor') || 'Sensor'}</Table.Th>
                  <Table.Th>Metrik</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>{translateMetricId('temperature')}</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Value</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>{translateMetricId('battery')}</Table.Th>
                  {selectedMetrics.includes('temperature') && <Table.Th style={{ textAlign: 'right' }}>{translateMetricId('temperature')}</Table.Th>}
                  {selectedMetrics.includes('humidity.general') && <Table.Th style={{ textAlign: 'right' }}>{translateMetricId('humidity.general')}</Table.Th>}
                  {selectedMetrics.includes('battery') && <Table.Th style={{ textAlign: 'right' }}>{translateMetricId('battery')}</Table.Th>}
                  {selectedMetrics.includes('spannung') && <Table.Th style={{ textAlign: 'right' }}>{translateMetricId('spannung')}</Table.Th>}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={7 + selectedMetrics.length}>
                      <Text c="dimmed">{t('data.table.noData') || 'Keine Daten gefunden'}</Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  data.map((r) => {
                    const unit = r?.sensor?.valueTypeValue || '';
                    
                    return (
                      <Table.Tr key={r.id}>
                        <Table.Td>{r?.timestamp ? new Date(r.timestamp).toLocaleString() : '-'}</Table.Td>
                        <Table.Td>{r?.gateway ? r.gateway.name : '-'}</Table.Td>
                        <Table.Td>{r?.sensor ? r.sensor.name : '-'}</Table.Td>
                        <Table.Td>{r?.sensor?.valueTypeName || '-'}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{r.temperature !== null && r.temperature !== undefined ? `${r.temperature} °C` : '-'}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{r.humidity_general !== null && r.humidity_general !== undefined ? `${r.humidity_general}${unit ? ` ${unit}` : ''}` : '-'}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{r.battery !== null && r.battery !== undefined ? `${r.battery} V` : '-'}</Table.Td>
                        {selectedMetrics.includes('temperature') && <Table.Td style={{ textAlign: 'right' }}>{r.temperature !== null && r.temperature !== undefined ? `${r.temperature} °C` : '-'}</Table.Td>}
                        {selectedMetrics.includes('humidity.general') && <Table.Td style={{ textAlign: 'right' }}>{r.humidity_general !== null && r.humidity_general !== undefined ? `${r.humidity_general} %` : '-'}</Table.Td>}
                        {selectedMetrics.includes('battery') && <Table.Td style={{ textAlign: 'right' }}>{r.battery !== null && r.battery !== undefined ? `${r.battery} %` : '-'}</Table.Td>}
                        {selectedMetrics.includes('spannung') && <Table.Td style={{ textAlign: 'right' }}>{r.spannung !== null && r.spannung !== undefined ? `${r.spannung} V` : '-'}</Table.Td>}
                      </Table.Tr>
                    );
                  })
                )}
              </Table.Tbody>
            </Table>
          )
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: rem(12) }}>
          <Text c="dimmed">{t('data.table.total', { count: total }) || `${total} gesamt`}</Text>
          <Group gap="xs" align="center">
            <Tooltip label={t('data.table.exportCsv') || 'Alle Daten als CSV exportieren'}>
              <ActionIcon
                variant="subtle"
                loading={exportLoading}
                onClick={async () => {
                  try {
                    setExportLoading(true);
                    // Warn if too many records
                    if (total > 100000) {
                      const confirmed = window.confirm(
                        `${total} Datensätze werden exportiert. Das kann eine Weile dauern. Fortfahren?`
                      );
                      if (!confirmed) {
                        setExportLoading(false);
                        return;
                      }
                    }
                    
                    // Fetch all data with large pageSize using authenticated API client
                    const allData: PivotedMeasurementRecord[] = [];
                    let page = 1;
                    let hasMore = true;
                    const pageSize = 1000;
                    
                    while (hasMore) {
                      const queryParams: DataQuery = {
                        page,
                        pageSize,
                        metric: selectedMetrics.length ? selectedMetrics : undefined,
                        gatewayId: query.gatewayId,
                        sensorId: query.sensorId,
                        from: query.from,
                        to: query.to,
                      };
                      
                      const result = await fetchMeasurements(queryParams);
                      allData.push(...(result.items || []));
                      hasMore = (result.items?.length || 0) === pageSize;
                      page++;
                    }
                    
                    // Build CSV with all displayed columns
                    const headerBase = ['Date','Time','Gateway','Sensor','Metric','Temperature (°C)','Value','Battery (V)'];
                    const metricHeaders: string[] = [];
                    if (selectedMetrics.includes('temperature')) metricHeaders.push('Temperature (°C)');
                    if (selectedMetrics.includes('humidity.general')) metricHeaders.push('Humidity General (%)');
                    if (selectedMetrics.includes('battery')) metricHeaders.push('Battery (%)');
                    if (selectedMetrics.includes('spannung')) metricHeaders.push('Voltage (V)');
                    const header = [...headerBase, ...metricHeaders];
                    
                    const rows = allData.map((r) => {
                      const unit = r?.sensor?.valueTypeValue || '';
                      // Format timestamp as dd.mm.yyyy and hh:mm:ss
                      const date = new Date(r.timestamp);
                      const day = String(date.getDate()).padStart(2, '0');
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const year = date.getFullYear();
                      const hours = String(date.getHours()).padStart(2, '0');
                      const minutes = String(date.getMinutes()).padStart(2, '0');
                      const seconds = String(date.getSeconds()).padStart(2, '0');
                      const formattedDate = `${day}.${month}.${year}`;
                      const formattedTime = `${hours}:${minutes}:${seconds}`;
                      
                      const baseRow = [
                        formattedDate,
                        formattedTime,
                        r.gateway ? r.gateway.name : '',
                        r.sensor ? r.sensor.name : '',
                        r?.sensor?.valueTypeName || '',
                        r.temperature !== null && r.temperature !== undefined ? `${r.temperature}` : '',
                        r.humidity_general !== null && r.humidity_general !== undefined ? `${r.humidity_general}${unit ? ` ${unit}` : ''}` : '',
                        r.battery !== null && r.battery !== undefined ? `${r.battery}` : '',
                      ];
                      const metricValues: string[] = [];
                      if (selectedMetrics.includes('temperature')) metricValues.push(r.temperature !== null && r.temperature !== undefined ? String(r.temperature) : '');
                      if (selectedMetrics.includes('humidity.general')) metricValues.push(r.humidity_general !== null && r.humidity_general !== undefined ? String(r.humidity_general) : '');
                      if (selectedMetrics.includes('battery')) metricValues.push(r.battery !== null && r.battery !== undefined ? String(r.battery) : '');
                      if (selectedMetrics.includes('spannung')) metricValues.push(r.spannung !== null && r.spannung !== undefined ? String(r.spannung) : '');
                      return [...baseRow, ...metricValues];
                    });
                    const csv = [header, ...rows]
                      .map((arr) => arr.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
                      .join('\n');
                    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `messwerte_alle_${new Date().toISOString().split('T')[0]}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                    setExportLoading(false);
                  } catch (e) {
                    const msg = e instanceof Error ? e.message : String(e);
                    alert(`Export fehlgeschlagen: ${msg}`);
                    setExportLoading(false);
                  }
                }}
                aria-label="export-all-csv"
              >
                <IconDownload size={18} />
              </ActionIcon>
            </Tooltip>
            <Select
              placeholder="Pro Seite"
              data={['10', '25', '50', '100']}
              value={String(query.pageSize || 10)}
              onChange={(val) => setQuery((q) => ({ ...q, pageSize: parseInt(val || '10'), page: 1 }))}
              w={100}
              size="sm"
              searchable={false}
              clearable={false}
            />
            <Pagination
              total={totalPages}
              value={query.page || 1}
              onChange={(p) => setQuery((q) => ({ ...q, page: p }))}
            />
          </Group>
        </div>
      </Card>
    </Stack>
  );
};

export default MeasurementsPage;
